import {
  Coins,
  Clock,
  AlertTriangle,
  MapPin,
  PencilOff,
  HeartHandshake,
  Home,
  Check,
} from 'lucide-react';
import type { ComputedBadge, BadgeId } from '../lib/computedBadges';

// =============================================================================
// ComputedBadges (v0.2.31)
//
// Renders the small visual badges produced by lib/computedBadges.ts. Each
// badge has a fixed icon and a color family. Colors are chosen so the
// urgent ones (Overdue, Due soon) read at a glance and the descriptive ones
// stay quieter.
// =============================================================================

const ICONS: Record<BadgeId, typeof Coins> = {
  high_value: Coins,
  due_soon: Clock,
  overdue: AlertTriangle,
  local: MapPin,
  no_essay: PencilOff,
  need_based: HeartHandshake,
  homeschool_friendly: Home,
  submitted: Check,
};

// Tailwind color classes per badge (bg + text), kept muted but distinct.
const COLORS: Record<BadgeId, string> = {
  high_value: 'bg-green-100 text-green-800',
  due_soon: 'bg-amber-100 text-amber-800',
  overdue: 'bg-rose-100 text-rose-800',
  local: 'bg-sky-100 text-sky-800',
  no_essay: 'bg-teal-100 text-teal-800',
  need_based: 'bg-forest-100 text-forest-800',
  homeschool_friendly: 'bg-pink-100 text-pink-800',
  submitted: 'bg-line text-soft',
};

export function ComputedBadges({
  badges,
  size = 'sm',
}: {
  badges: ComputedBadge[];
  size?: 'sm' | 'xs';
}) {
  if (badges.length === 0) return null;
  const pad = size === 'xs' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs';
  const iconSize = size === 'xs' ? 11 : 12;

  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((b) => {
        const Icon = ICONS[b.id];
        return (
          <span
            key={b.id}
            className={`inline-flex items-center gap-1 rounded font-medium ${pad} ${COLORS[b.id]}`}
            title={b.label}
          >
            <Icon size={iconSize} />
            {b.label}
          </span>
        );
      })}
    </div>
  );
}
